import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { InvoiceEntity } from './invoice.entity';
import { MPaymentTransactionEntity } from './m-payment-transaction.entity';
import { PaymentScheduleEntity } from './payment-schedule.entity';

/**
 * Occurrence status lifecycle:
 * - pending: Waiting for scheduled date to arrive
 * - processing: Currently being processed (lock acquired)
 * - succeeded: Payment captured successfully
 * - failed: All retry attempts exhausted
 * - skipped: Invoice already paid or other reason to skip
 * - cancelled: Parent schedule was cancelled or paused
 */
export type PaymentScheduleOccurrenceStatus =
	| 'pending'
	| 'processing'
	| 'succeeded'
	| 'failed'
	| 'skipped'
	| 'cancelled';

@Index(
	'payment_schedule_occurrence_status_scheduled_date',
	['status', 'scheduledDate'],
	{},
)
@Index(
	'payment_schedule_occurrence_status_next_retry_date',
	['status', 'nextRetryDate'],
	{},
)
@Index(
	'payment_schedule_occurrence_fk_payment_schedule__id',
	['fkPaymentScheduleId'],
	{},
)
@Index(
	'payment_schedule_occurrence_fk_transaction__id',
	['fkTransactionId'],
	{},
)
@Entity(
	'payment_schedule_occurrence',
	{},
	{ label: 'Payment Schedule Occurrence' },
)
export class PaymentScheduleOccurrenceEntity extends BaseEntity {
	@Column('int', {
		name: '_fk_payment_schedule__id',
		unsigned: true,
		comment: 'Parent schedule this occurrence belongs to',
	})
	public fkPaymentScheduleId!: number;

	@Column('int', {
		name: '_fk_invoice__id',
		nullable: true,
		unsigned: true,
		comment: 'Which invoice this occurrence pays (for autopay/installment)',
	})
	public fkInvoiceId!: number | null;

	@Column('int', {
		name: '_fk_transaction__id',
		nullable: true,
		unsigned: true,
		comment: 'Resulting transaction after successful processing',
	})
	public fkTransactionId!: number | null;

	@Column('date', {
		name: 'scheduledDate',
		comment: 'When this payment is scheduled to execute',
	})
	public scheduledDate!: string;

	@Column('datetime', {
		name: 'executedDate',
		nullable: true,
		comment: 'When this payment was actually processed',
	})
	public executedDate!: Date | null;

	@Column('decimal', {
		name: 'amount',
		precision: 13,
		scale: 2,
		comment: 'Target payment amount',
	})
	public amount!: string;

	@Column('decimal', {
		name: 'actualAmount',
		nullable: true,
		precision: 13,
		scale: 2,
		comment:
			'Amount actually charged (may differ if invoice was partially paid manually)',
	})
	public actualAmount!: string | null;

	@Column('varchar', {
		name: 'status',
		length: 32,
		default: () => "'pending'",
		comment: 'Current processing status of this occurrence',
	})
	public status!: PaymentScheduleOccurrenceStatus;

	@Column('smallint', {
		name: 'retryCount',
		default: 0,
		comment: 'Number of retry attempts made so far',
	})
	public retryCount!: number;

	@Column('datetime', {
		name: 'nextRetryDate',
		nullable: true,
		comment: 'When to attempt the next retry (if failed and retries remain)',
	})
	public nextRetryDate!: Date | null;

	@Column('text', {
		name: 'lastError',
		nullable: true,
		comment: 'Error message from the most recent failure',
	})
	public lastError!: string | null;

	@Column('varchar', {
		name: 'lastErrorCode',
		nullable: true,
		length: 64,
		comment: 'Error code from the payment gateway',
	})
	public lastErrorCode!: string | null;

	// --- Relationships ---

	@ManyToOne(() => PaymentScheduleEntity, (schedule) => schedule.occurrences, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: '_fk_payment_schedule__id', referencedColumnName: 'id' },
	])
	public fkPaymentSchedule!: PaymentScheduleEntity;

	@ManyToOne(() => InvoiceEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_invoice__id', referencedColumnName: 'id' }])
	public fkInvoice!: InvoiceEntity;

	@ManyToOne(() => MPaymentTransactionEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_transaction__id', referencedColumnName: 'id' }])
	public fkTransaction!: MPaymentTransactionEntity;
}
